import type { Localization } from '@/shared/components/query-builder'
import type { TFunction } from 'i18next'

import { Operators } from '@/shared/components/query-builder'
import { getDatesLocale } from '@/shared/i18n'

export const getLocalization = (t: TFunction): Localization => ({
	getCombinatorLabel(joinOperator: 'and' | 'or') {
		return t(joinOperator, { defaultValue: joinOperator })
	},
	getWhereLabel() {
		return t('where', { defaultValue: 'Where' })
	},
	getOperatorLabel(operatorKey: string | Operators) {
		return getDefaultOperatorsNameMapping(t)[operatorKey] || operatorKey
	},
	getFieldPlaceholder() {
		return t('general_select', { defaultValue: 'Select' })
	},
	getOperationPlaceholder() {
		return t('general_select', { defaultValue: 'Select' })
	},
	getDuplicatedRowsMessage() {
		return 'Conditions conflict! Only the last duplicate filter will be applied.'
	},
	getSelectValuePlaceholder() {
		return 'Select a value'
	},
	getEnterValuePlaceholder() {
		return 'Enter a value'
	},
	getSearchPlaceholder() {
		return t('placeholder.search_options', {
			defaultValue: 'Search options...',
		})
	},
	getNoOptionsMessage() {
		return t('no_options', { defaultValue: 'No options found.' })
	},
	getDateFnsLocalization() {
		return getDatesLocale()
	},
	getSelectAllMessage() {
		return t('general_select_all', { defaultValue: 'Select all' })
	},
	getReferenceTypeField() {
		return t('ref_type.field', { defaultValue: 'Field' })
	},
	getReferenceTypeValue() {
		return t('ref_type.value', { defaultValue: 'Value' })
	},
	getSelectedCountMessage(count: number) {
		return t('app.selected_count', {
			count,
			defaultValue: `Selected (${count})`,
		})
	},
})

const getDefaultOperatorsNameMapping = (
	t: TFunction
): Record<string, string> => ({
	[Operators.CONTAINS]: t('op.contains', { defaultValue: 'contains' }),
	[Operators.NOT_CONTAINS]: t('op.not_contains', {
		defaultValue: 'not contains',
	}),
	[Operators.IS]: t('op.is', { defaultValue: 'is' }),
	[Operators.IN]: t('op.in', { defaultValue: 'in' }),
	[Operators.NOT_IN]: t('op.not_in', { defaultValue: 'not in' }),
	[Operators.IS_NOT]: t('op.is_not', { defaultValue: 'is not' }),
	[Operators.IS_EMPTY]: t('op.is_empty', { defaultValue: 'is empty' }),
	[Operators.IS_NOT_EMPTY]: t('op.is_not_empty', {
		defaultValue: 'is not empty',
	}),
	[Operators.GT]: '>',
	[Operators.LT]: '<',
	[Operators.EQ]: '=',
	[Operators.LTE]: '≤',
	[Operators.GTE]: '≥',
	[Operators.NE]: '≠',
	[Operators.BL]: '=',
	[Operators.ANY_OF]: t('op.any_of', { defaultValue: 'any of' }),
	[Operators.ALL_OF]: t('op.all_of', { defaultValue: 'all of' }),
	[Operators.EXACT]: t('op.exact', { defaultValue: 'is exactly' }),
	[Operators.NONE_OF]: t('op.none_of', { defaultValue: 'is none of' }),
	[Operators.BETWEEN]: t('op.between', { defaultValue: 'between' }),
	[Operators.DATE_AFTER]: t('op.after', { defaultValue: 'after' }),
	[Operators.DATE_BEFORE]: t('op.before', { defaultValue: 'before' }),
	[Operators.DATE_AT]: t('op.at', { defaultValue: 'at' }),
	[Operators.DATE_BETWEEN]: t('op.between', { defaultValue: 'between' }),
})
